import {
  PromoTilesItem,
  PromoTilesSlice,
} from '@adapters/prismic/types/brandedStorePage';
import {
  PromoTile,
  PromoTileCta,
  PromoTilesData,
} from '_components/PromoTiles/types';

const toTile = (item: PromoTilesItem): PromoTile | null => {
  const heading = item.heading?.trim();
  const desktopImage = item.image?.url ? item.image : item.mobile_image;
  const mobileImage = item.mobile_image?.url ? item.mobile_image : item.image;
  const imageUrl = desktopImage?.url;
  const mobileImageUrl = mobileImage?.url;

  const cta1Label = item.cta_1_label?.trim();
  const cta1Link = item.cta_1_link?.trim();
  const cta2Label = item.cta_2_label?.trim();
  const cta2Link = item.cta_2_link?.trim();

  // Slots are the authored position, not the surviving index: a tile with only
  // cta_2 filled in keeps slot 1 so ED-421's datalayer push does not report it
  // as the primary CTA.
  const ctas: PromoTileCta[] = [
    cta1Label && cta1Link ? { label: cta1Label, url: cta1Link, slot: 0 } : null,
    cta2Label && cta2Link ? { label: cta2Label, url: cta2Link, slot: 1 } : null,
  ].filter((cta): cta is PromoTileCta => cta !== null);

  // Heading and CTAs are optional so a tile can be image-only (e.g. artwork
  // with its copy baked in). A tile with no photo is a hole in the row, so it
  // is dropped here and `image` stays required on PromoTile.
  if (!imageUrl) return null;

  const body = item.body?.trim();
  const coOpBrand = item.co_op_brand?.trim();
  const alt = desktopImage?.alt ?? '';

  return {
    heading: heading ?? '',
    ...(body ? { body } : {}),
    ctas,
    image: { url: imageUrl, alt },
    // Only a distinct upload is worth a second <img> and the breakpoint toggle.
    ...(mobileImageUrl && mobileImageUrl !== imageUrl
      ? { mobileImage: { url: mobileImageUrl, alt: mobileImage?.alt ?? alt } }
      : {}),
    coOpBrand: coOpBrand || null,
  };
};

/**
 * Maps a Prismic `promo_tiles` slice into the shape `_components/PromoTiles`
 * consumes, trimming every authored string and dropping half-authored rows.
 *
 * Takes the two slice zones rather than the slice, matching mapLearnStripData.
 * The CLP slice union includes a catch-all whose `slice_type` is a plain string,
 * so a whole-slice parameter would not narrow at that call site.
 */
const mapPromoTilesData = (
  primary: PromoTilesSlice['primary'] | undefined,
  items: PromoTilesItem[] | undefined
): PromoTilesData => ({
  heading: primary?.title?.trim() ?? '',
  tiles: (items ?? [])
    .map(toTile)
    .filter((tile): tile is PromoTile => tile !== null),
});

export default mapPromoTilesData;
